import { Permission } from "@/shared/auth/permissions";
import { useCanDo } from "@/shared/auth/use-can-do";
import { useCredits } from "@/shared/entitlements/hooks/use-credits";

const OUT_OF_CREDITS_REASON = "You are out of AI credits this month.";

export interface WritingAssistGate {
  canAssist: boolean;
  /// Set only when the block is specifically the credit pool (permission
  /// granted, no credits left). A permission-only block resolves to
  /// `undefined` here. Callers keep hiding the affordance for that case
  /// exactly like they did before credits existed, no reason invented.
  disabledReason?: string;
}

/// Pure resolver, extracted so the combination logic is unit-testable
/// without a render harness (this repo has no RTL). Follows the
/// `resolveFeatureEnabled` / `resolveMeterState` precedent in
/// `shared/entitlements`. `remaining === null` means an unlimited credit
/// grant, same convention as `Entitlements["credits"]`.
export function resolveWritingAssistGate({
  hasPermission,
  remaining,
}: {
  hasPermission: boolean;
  remaining: number | null;
}): WritingAssistGate {
  const hasCredits = remaining === null || remaining > 0;
  if (hasPermission && !hasCredits) {
    return { canAssist: false, disabledReason: OUT_OF_CREDITS_REASON };
  }
  return { canAssist: hasPermission && hasCredits };
}

/// Combines the WRITING_ASSISTANT_GENERATE permission with the account's AI
/// credit pool: the server already 429s a generate call when credits run
/// out (SEC5), this only reflects that in the UI so the button doesn't
/// invite a request that's certain to fail.
export function useWritingAssistGate(): WritingAssistGate {
  const hasPermission = useCanDo(Permission.WRITING_ASSISTANT_GENERATE);
  const { remaining } = useCredits();
  return resolveWritingAssistGate({ hasPermission, remaining });
}
